import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  chartOfAccountsService,
  generalLedgerService,
  GLAccountDTO,
  GLAccountLedgerLineDTO,
} from '../../../../services/accountingService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { ArrowLeft } from 'lucide-react';

const AccountLedgerPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [account, setAccount] = useState<GLAccountDTO | null>(null);
  const [lines, setLines] = useState<GLAccountLedgerLineDTO[]>([]);
  const [loading, setLoading] = useState(true);

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmtDate = (d: string) => new Date(d).toLocaleDateString(langCode);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      chartOfAccountsService.getById(Number(id)),
      generalLedgerService.getAccountLedger(Number(id)),
    ]).then(([accRes, ledgerRes]) => {
      setAccount((accRes?.data as any)?.data ?? accRes?.data ?? null);
      const ledgerData = (ledgerRes?.data as any)?.data ?? ledgerRes?.data ?? [];
      setLines(Array.isArray(ledgerData) ? ledgerData : []);
    }).finally(() => setLoading(false));
  }, [id]);

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 960, margin: '0 auto' }}>
        <div className="ims-top-bar" style={{ marginBottom: 20 }}>
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/accounting/chart-of-accounts')}>
              <ArrowLeft size={13} /> {t('accounting.coa.title')}
            </button>
            <div>
              <h1 className="ims-page-title">{account ? `${account.code} — ${account.name}` : t('common.loading')}</h1>
              {account && <p className="ims-page-subtitle">{t(`accounting.accountTypes.${account.accountType}`)} · {t('accounting.coa.normalBalance')}: {t(`accounting.normalBalance.${account.normalBalance}`)}</p>}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : lines.length === 0 ? (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            {t('accounting.ledger.noActivity')}
          </div>
        ) : (
          <div className="ims-card">
            <div className="ims-table-scroll">
              <table className="ims-table">
                <thead>
                  <tr>
                    <th className="ims-th" style={{ width: 110 }}>{t('accounting.ledger.date')}</th>
                    <th className="ims-th" style={{ width: 140 }}>{t('accounting.ledger.entryNumber')}</th>
                    <th className="ims-th">{t('common.description')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 120 }}>{t('accounting.ledger.debit')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 120 }}>{t('accounting.ledger.credit')}</th>
                    <th className="ims-th ims-th--num" style={{ width: 130 }}>{t('accounting.ledger.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => (
                    <tr key={idx} className="ims-tr">
                      <td className="ims-td">{fmtDate(l.transactionDate)}</td>
                      <td className="ims-td"><code>{l.entryNumber}</code></td>
                      <td className="ims-td">{l.description || '—'}</td>
                      <td className="ims-td ims-td--line-total">{l.debit > 0 ? fmt(l.debit) : ''}</td>
                      <td className="ims-td ims-td--line-total">{l.credit > 0 ? fmt(l.credit) : ''}</td>
                      <td className="ims-td ims-td--line-total"><strong>{fmt(l.runningBalance)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AccountLedgerPage;
